import React from 'react';
import { getScoreBand, getEpsColor } from '@/lib/scoring';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface EpsHeroCardProps {
  eps: number;
  previousEps?: number;
  athleteName: string;
  sessionDate: string;
  primaryPriority: string;
}

export default function EpsHeroCard({ eps, previousEps, athleteName, sessionDate, primaryPriority }: EpsHeroCardProps) {
  const band = getScoreBand(eps);
  const color = getEpsColor(eps);
  const delta = previousEps !== undefined ? eps - previousEps : null;

  return (
    <div
      className="rounded-xl p-6 col-span-2 relative overflow-hidden smoke-overlay"
      style={{
        background: `rgba(20,20,20,0.9)`,
        border: `1px solid rgba(225,6,0,0.2)`,
        boxShadow: `0 0 40px rgba(225,6,0,0.08), inset 0 1px 0 rgba(255,255,255,0.04)`,
      }}
    >
      {/* Background radial glow */}
      <div
        className="absolute top-0 right-0 w-72 h-72 rounded-full -translate-y-20 translate-x-20 pointer-events-none"
        style={{ background: `radial-gradient(circle, ${color}12 0%, transparent 70%)` }}
      />
      {/* Smoke fog overlay */}
      <div
        className="absolute bottom-0 left-0 w-full h-1/2 pointer-events-none"
        style={{ background: 'linear-gradient(to top, rgba(255,255,255,0.012) 0%, transparent 100%)' }}
      />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-zinc-500 mb-1">Explosive Power Score</p>
            <p className="text-sm text-zinc-400">{athleteName} · {sessionDate}</p>
          </div>
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{ backgroundColor: `${color}18`, color, border: `1px solid ${color}30` }}
          >
            {band.label}
          </span>
        </div>

        <div className="flex items-end gap-4 mb-4">
          <span className="font-mono tabular-nums font-black text-7xl leading-none" style={{ color }}>
            {eps}
          </span>
          <div className="mb-2">
            <span className="text-zinc-600 text-xl">/100</span>
            {delta !== null && (
              <div className={`flex items-center gap-1 text-sm font-semibold mt-1 ${delta > 0 ? 'text-emerald-400' : delta < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                {delta > 0 ? <TrendingUp size={14} /> : delta < 0 ? <TrendingDown size={14} /> : <Minus size={14} />}
                <span className="font-mono">{delta > 0 ? '+' : ''}{delta}</span>
                <span className="font-sans font-normal text-zinc-500">vs last</span>
              </div>
            )}
          </div>
        </div>

        {/* Score bar */}
        <div className="h-1.5 rounded-full mb-3 overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
          <div
            className="h-full rounded-full transition-all duration-700"
            style={{
              width: `${eps}%`,
              background: `linear-gradient(90deg, #8B0000 0%, ${color} 100%)`,
              boxShadow: `0 0 8px ${color}60`,
            }}
          />
        </div>

        <p className="text-xs text-zinc-500">
          <span className="text-zinc-400 font-medium">Priority:</span> {primaryPriority}
        </p>
      </div>
    </div>
  );
}